import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, Store, Check, Loader2 } from 'lucide-react';

const RegisterShop = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const [formData, setFormData] = useState({
    shopName: '',
    address: '',
    city: '',
    state: '',
    shopPhoneNumber: '',
    contactPerson: '',
    contactNumber: '',
    websiteLink: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.shopName.trim()) newErrors.shopName = 'Shop name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.shopPhoneNumber.trim()) newErrors.shopPhoneNumber = 'Phone number is required';
    if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact person is required';
    if (!formData.contactNumber.trim()) newErrors.contactNumber = 'Contact number is required';
    
    // Validate website URL if provided
    if (formData.websiteLink && formData.websiteLink.trim()) {
      try {
        new URL(formData.websiteLink);
      } catch {
        newErrors.websiteLink = 'Invalid website URL';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError('');

    try {
      const payload = {
        shopName: formData.shopName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        shopPhoneNumber: formData.shopPhoneNumber,
        contactPerson: formData.contactPerson,
        contactNumber: formData.contactNumber,
      };

      // Only include websiteLink if provided
      if (formData.websiteLink && formData.websiteLink.trim()) {
        payload.websiteLink = formData.websiteLink;
      }

      const response = await apiService.registerShop(payload);

      if (response.success) {
        setIsSuccess(true);
        setTimeout(() => {
          navigate('/shops');
        }, 2000);
      } else {
        setApiError(response.message || 'Failed to register shop');
      }
    } catch (error) {
      setApiError(error.message || 'Failed to register shop');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="p-6 md:p-8" data-testid="register-success">
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#22C55E]/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#4ADE80]" />
            </div>
            <h2 className="text-2xl font-bold text-[#E2E8F0] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Shop Registered Successfully!
            </h2>
            <p className="text-[#94A3B8]">
              Redirecting to shops list...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="register-shop-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/shops')}
          className="text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#334155]/50 mb-4 -ml-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Shops
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#2563EB]/20 flex items-center justify-center">
            <Store className="w-6 h-6 text-[#60A5FA]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Register Auto Shop
            </h1>
            <p className="text-[#94A3B8]">Add a new auto shop to the system</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="bg-[#1E293B] border border-[#334155] rounded-lg p-6 md:p-8 space-y-6">
          {/* API Error */}
          {apiError && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3">
              <p className="text-[#EF4444] text-sm">{apiError}</p>
            </div>
          )}

          {/* Shop Name */}
          <div className="space-y-2">
            <Label htmlFor="shopName" className="text-[#E2E8F0] text-sm font-medium">
              Shop Name <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="shopName"
              value={formData.shopName}
              onChange={(e) => handleChange('shopName', e.target.value)}
              placeholder="Enter shop name"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.shopName ? 'border-[#EF4444]' : ''}`}
              data-testid="shop-name-input"
            />
            {errors.shopName && <p className="text-[#EF4444] text-xs">{errors.shopName}</p>}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-[#E2E8F0] text-sm font-medium">
              Address <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Enter street address"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.address ? 'border-[#EF4444]' : ''}`}
              data-testid="address-input"
            />
            {errors.address && <p className="text-[#EF4444] text-xs">{errors.address}</p>}
          </div>

          {/* City and State */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="text-[#E2E8F0] text-sm font-medium">
                City <span className="text-[#EF4444]">*</span>
              </Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="Enter city"
                className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.city ? 'border-[#EF4444]' : ''}`}
                data-testid="city-input"
              />
              {errors.city && <p className="text-[#EF4444] text-xs">{errors.city}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="state" className="text-[#E2E8F0] text-sm font-medium">
                State <span className="text-[#EF4444]">*</span>
              </Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
                placeholder="Enter state (e.g., CA)"
                className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.state ? 'border-[#EF4444]' : ''}`}
                data-testid="state-input"
              />
              {errors.state && <p className="text-[#EF4444] text-xs">{errors.state}</p>}
            </div>
          </div>

          {/* Phone Numbers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shopPhoneNumber" className="text-[#E2E8F0] text-sm font-medium">
                Shop Phone <span className="text-[#EF4444]">*</span>
              </Label>
              <Input
                id="shopPhoneNumber"
                value={formData.shopPhoneNumber}
                onChange={(e) => handleChange('shopPhoneNumber', e.target.value)}
                placeholder="310-555-0101"
                className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.shopPhoneNumber ? 'border-[#EF4444]' : ''}`}
                data-testid="phone-input"
              />
              {errors.shopPhoneNumber && <p className="text-[#EF4444] text-xs">{errors.shopPhoneNumber}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactNumber" className="text-[#E2E8F0] text-sm font-medium">
                Contact Number <span className="text-[#EF4444]">*</span>
              </Label>
              <Input
                id="contactNumber"
                value={formData.contactNumber}
                onChange={(e) => handleChange('contactNumber', e.target.value)}
                placeholder="310-555-0202"
                className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.contactNumber ? 'border-[#EF4444]' : ''}`}
                data-testid="contact-number-input"
              />
              {errors.contactNumber && <p className="text-[#EF4444] text-xs">{errors.contactNumber}</p>}
            </div>
          </div>

          {/* Contact Person */}
          <div className="space-y-2">
            <Label htmlFor="contactPerson" className="text-[#E2E8F0] text-sm font-medium">
              Contact Person <span className="text-[#EF4444]">*</span>
            </Label>
            <Input
              id="contactPerson"
              value={formData.contactPerson}
              onChange={(e) => handleChange('contactPerson', e.target.value)}
              placeholder="Enter contact person name"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.contactPerson ? 'border-[#EF4444]' : ''}`}
              data-testid="contact-person-input"
            />
            {errors.contactPerson && <p className="text-[#EF4444] text-xs">{errors.contactPerson}</p>}
          </div>

          {/* Website (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="websiteLink" className="text-[#E2E8F0] text-sm font-medium">
              Website <span className="text-[#64748B]">(Optional)</span>
            </Label>
            <Input
              id="websiteLink"
              value={formData.websiteLink}
              onChange={(e) => handleChange('websiteLink', e.target.value)}
              placeholder="https://example.com"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11 ${errors.websiteLink ? 'border-[#EF4444]' : ''}`}
              data-testid="website-input"
            />
            {errors.websiteLink && <p className="text-[#EF4444] text-xs">{errors.websiteLink}</p>}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/shops')}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:text-[#E2E8F0]"
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex-1 sm:flex-none"
              data-testid="submit-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                'Register Shop'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterShop;
